import {
  Box,
  Button,
  Center,
  Container,
  Grid,
  Image,
  Skeleton,
  Tabs,
  Text,
  Title,
} from '@mantine/core'
import type { LoaderFunctionArgs } from '@remix-run/node'
import { Await, type MetaFunction, data, useLoaderData } from '@remix-run/react'
import { Suspense, useState } from 'react'
import { searchCompanyByIDQuery, searchEventsByCompanyQuery } from '~/bootstrap'
import { Breadcrumbs } from '~/components/breadcrumbs'
import {
  CompanyDetail,
  SkeletonCompanyDetail,
} from '~/components/company-detail'
import { EventCard, SkeletonEventCard } from '~/components/event-card'
import { JobDescription } from '~/components/job-description'
import MultiLineText from '~/components/multi-line-text'
import { NotFound } from '~/components/not-found'
import type { SearchCompanyByIDQueryOutput } from '~/core/company/application/search-company-by-id-query'
import { generateMetaDescriptor } from '~/core/shared/utils/misc'

const CompanySkeleton = () => {
  return (
    <Container>
      <Box mt='xl'>
        <Grid>
          <Grid.Col span={2}>
            <Skeleton height={104} width={104} maw={250} />
          </Grid.Col>
          <Grid.Col span={10}>
            <Skeleton height={40} />
          </Grid.Col>
        </Grid>

        <Skeleton height={20} w='100%' mt='sm' />
        <Skeleton height={20} w='100%' mt='sm' />
        <Skeleton height={20} w='100%' mt='sm' />
        <Skeleton height={20} w='100%' mt='sm' />

        <Center>
          <Skeleton height={40} w={180} mt='sm' />
        </Center>

        <Tabs color='primary' mt='xl'>
          <Tabs.List grow justify='center'>
            <Tabs.Tab value='CompanyDetail'>
              <Title order={4} c='primary'>
                企業データ
              </Title>
            </Tabs.Tab>
            <Tabs.Tab value='JobDescription'>
              <Title order={4} c='secondary'>
                募集要項
              </Title>
            </Tabs.Tab>
          </Tabs.List>
          <SkeletonCompanyDetail />
        </Tabs>
      </Box>
    </Container>
  )
}

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const id = params.id || ''

  const url = new URL(request.url)
  const previewToken = url.searchParams.get('previewToken')

  let companies:
    | SearchCompanyByIDQueryOutput
    | Promise<SearchCompanyByIDQueryOutput>

  if (previewToken) {
    // プレビューの場合AwaitしpreviewTokenをチェック
    companies = await searchCompanyByIDQuery.execute({
      id,
      limitToPublishedCompanies: true,
      draftMode: true,
    })
    const company = companies.at(0)

    if (company?.previewToken !== previewToken) {
      // 一致しない場合は空の配列にし、画面で「見つかりませんでした」を表示
      companies = []
    }
  } else {
    companies = searchCompanyByIDQuery.execute({
      id,
      limitToPublishedCompanies: true,
    })
  }

  const eventsPromise = searchEventsByCompanyQuery.execute({
    id,
    limitToOpenEvents: true,
  })

  const company = (await companies).at(0)
  return data({
    companyPromise: companies,
    eventsPromise,
    meta: {
      title: company?.name,
      description: company?.introduction,
      image: company?.logoUrl,
    },
  })
}

export const meta: MetaFunction<typeof loader> = (metaInfo) =>
  generateMetaDescriptor(metaInfo, {
    pageTitle: metaInfo.data?.meta.title || '企業詳細',
    description: metaInfo.data?.meta.description,
    image: metaInfo.data?.meta.image,
  })

export default function Company() {
  const { companyPromise, eventsPromise } = useLoaderData<typeof loader>()
  const [activeTab, setActiveTab] = useState<string | null>('CompanyDetail')

  return (
    <>
      {/* PC */}
      <Box py='sm' px='xl' visibleFrom='sm'>
        <Suspense fallback={<div>Loading...</div>}>
          <Await resolve={companyPromise} errorElement={<div>エラー</div>}>
            {(companies) => {
              const company = companies.at(0)
              if (!company) {
                return null
              }
              return (
                <Breadcrumbs
                  items={[
                    {
                      id: 'companies',
                      title: '企業一覧',
                      href: '/companies',
                    },
                    {
                      id: `company-${company.id}`,
                      title: company.name,
                    },
                  ]}
                />
              )
            }}
          </Await>
        </Suspense>
      </Box>

      {/* SP */}
      <Box py='sm' px='lg' hiddenFrom='sm'>
        <Suspense fallback={<div>Loading...</div>}>
          <Await resolve={companyPromise} errorElement={<div>エラー</div>}>
            {(companies) => {
              const company = companies.at(0)
              if (!company) {
                return null
              }
              return (
                <Breadcrumbs
                  items={[
                    {
                      id: 'companies',
                      title: '企業一覧',
                      href: '/companies',
                    },
                    {
                      id: `company-${company.id}`,
                      title: company.name,
                    },
                  ]}
                />
              )
            }}
          </Await>
        </Suspense>
      </Box>

      <Container my='xl'>
        <Suspense fallback={<CompanySkeleton />}>
          <Await resolve={companyPromise} errorElement={<div>エラー</div>}>
            {(companies) => {
              const company = companies.at(0)
              if (!company) return <NotFound />

              return (
                <>
                  <Box mt='xl'>
                    <Grid>
                      <Grid.Col span={{ base: 4, sm: 3 }}>
                        <Image
                          w='100%'
                          maw={250}
                          fit='contain'
                          src={`${company.logoUrl}`}
                          alt={`${company.name}のロゴ画像`}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ base: 8, sm: 9 }}>
                        <Title order={1}>{company.name}</Title>
                      </Grid.Col>
                    </Grid>

                    <MultiLineText mt='lg'>
                      {company.introduction}
                    </MultiLineText>

                    <Center>
                      <Button
                        variant='outline'
                        color='primary'
                        mt='md'
                        component='a'
                        href='#events'
                      >
                        この企業のイベントを見る
                      </Button>
                    </Center>

                    <Tabs
                      value={activeTab}
                      onChange={setActiveTab}
                      color='primary'
                      mt='xl'
                    >
                      <Tabs.List grow justify='center'>
                        <Tabs.Tab value='CompanyDetail'>
                          <Text
                            fw='bold'
                            size='xl'
                            c={
                              activeTab === 'CompanyDetail'
                                ? 'primary'
                                : 'secondary'
                            }
                          >
                            企業データ
                          </Text>
                        </Tabs.Tab>
                        <Tabs.Tab value='JobDescription'>
                          <Text
                            fw='bold'
                            size='xl'
                            c={
                              activeTab === 'JobDescription'
                                ? 'primary'
                                : 'secondary'
                            }
                          >
                            募集要項
                          </Text>
                        </Tabs.Tab>
                      </Tabs.List>

                      <Tabs.Panel value='CompanyDetail'>
                        <Box my='lg'>
                          <CompanyDetail data={company} />
                        </Box>
                      </Tabs.Panel>

                      <Tabs.Panel value='JobDescription'>
                        <Box my='lg'>
                          <JobDescription data={company} />
                        </Box>
                      </Tabs.Panel>
                    </Tabs>

                    <Box mt={64}>
                      <Title order={3} c='primary' id='events'>
                        {company.name}のイベント情報
                      </Title>
                      <Suspense
                        fallback={
                          <Grid gutter='lg' mt={40}>
                            {Array.from({ length: 4 }).map((_, index) => {
                              const componentKey = `skeleton-company-event-${index}`
                              return (
                                <Grid.Col
                                  span={{ base: 12, sm: 4, lg: 3 }}
                                  h={366}
                                  key={componentKey}
                                >
                                  <SkeletonEventCard />
                                </Grid.Col>
                              )
                            })}
                          </Grid>
                        }
                      >
                        <Await resolve={eventsPromise}>
                          {(events) =>
                            events.searchResults.length > 0 ? (
                              <Grid gutter='lg' mt={40}>
                                {events.searchResults.map((event) => {
                                  return (
                                    <Grid.Col
                                      span={{ base: 12, sm: 6, lg: 3 }}
                                      key={event.id}
                                    >
                                      <EventCard
                                        url={`/events/${event.id}`}
                                        image={event.coverUrl}
                                        operator={event.organizerName}
                                        title={event.title}
                                      />
                                    </Grid.Col>
                                  )
                                })}
                              </Grid>
                            ) : (
                              <Text mt={40}>
                                現在公開されているイベントはありません。
                              </Text>
                            )
                          }
                        </Await>
                      </Suspense>
                    </Box>
                  </Box>
                </>
              )
            }}
          </Await>
        </Suspense>
      </Container>
    </>
  )
}
